"use client";

import Image from "next/image";
import { Heading } from "../design-system/atoms/Heading";
import { BlockProps } from "./blocks/registry";
import { resolveLegacyLink } from "../utils/linkUtils";
import { useEffect, useState } from "react";
import { buildProspectLink } from "../utils/prospectLink";
import { useMobileSlider } from "../hooks/useMobileSlider";
import PreCommandePopup from "./CommandePopup";

type StrapiMedia = {
  url?: string;
  alternativeText?: string;
};

type OffrirCard = {
  id?: number | string;
  titre?: string;
  descr?: string;
  icon?: StrapiMedia;
};

type OffrirCta = {
  id?: number | string;
  text?: string;
  link?: string;
};

type OffrirBlockData = {
  id?: number | string;
  documentId?: string;
  __component?: string;
  titre?: string;
  offre?: OffrirCard[];
  cta?: OffrirCta;
  [key: string]: unknown;
};

export default function OffrirBloc({ data }: BlockProps<OffrirBlockData>) {
  const offres = Array.isArray(data?.offre) ? data.offre : [];

  const { sliderRef, activeIndex, handleScroll, scrollToIndex } =
    useMobileSlider({
      itemsLength: offres.length,
    });

  const [commanderHref, setCommanderHref] = useState<string>(
    resolveLegacyLink("/parcours-prospects", "clients")
  );

  useEffect(() => {
    const link =
      buildProspectLink() || resolveLegacyLink("/parcours-prospects", "clients");

    setCommanderHref(link);
  }, []);

  const [showPreCommandePopup, setShowPreCommandePopup] = useState(false);

  if (!data) return null;

  return (
    <section className="py-10 bg-white px-6 overflow-hidden">
      {/* Titre */}
      <Heading
        as="h2"
        className="text-4xl lg:text-[44px] text-center font-bold text-text mb-6"
      >
        {data.titre}
      </Heading>

      {/* Wrapper pour empêcher le slider de casser la largeur */}
      <div className="w-full max-w-6xl mx-auto  overflow-hidden lg:overflow-visible">
        {/* Slider mobile + grid desktop */}
        <div
          ref={sliderRef}
          onScroll={handleScroll}
          className="
            flex w-full overflow-x-auto scroll-smooth snap-x snap-mandatory
            lg:grid lg:grid-cols-3 lg:gap-8 lg:overflow-visible lg:snap-none
            [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden
          "
        >
          {offres.map((card, index) => (
            <div
              key={card.id ?? index}
              className="
                basis-full shrink-0 snap-center px-2
                lg:contents
              "
            >
              <article
                className="
                  bg-white rounded-2xl shadow-md overflow-hidden flex flex-col
                  w-full
                "
              >
                <div className="relative w-full h-[320px] md:h-[420px] lg:h-[260px]">
                  <Image
                    src={card.icon?.url || "/fallback-image.jpg"}
                    alt={card.icon?.alternativeText || card.titre || ""}
                    fill
                    sizes="(max-width: 1024px) 100vw, 33vw"
                    className="object-cover"
                  />
                </div>

                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="text-xl font-semibold text-secondary mb-2">
                    {card.titre}
                  </h3>

                  <p className="text-gray-700 flex-1">
                    {card.descr}
                  </p>
                </div>
              </article>
            </div>
          ))}
        </div>
      </div>

      {/* Dots mobile seulement */}
      {offres.length > 1 && (
        <div className="flex justify-center gap-2 mt-6 lg:hidden">
          {offres.map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => scrollToIndex(index)}
              className={`h-2 rounded-full transition-all duration-300 ${activeIndex === index
                  ? "w-6 bg-secondary"
                  : "w-2 bg-gray-300"
                }`}
              aria-label={`Aller à l'offre ${index + 1}`}
            />
          ))}
        </div>
      )}

      <div className="flex justify-center mt-12">
        <button
          type="button"
          onClick={() => setShowPreCommandePopup(true)}
          className="bg-secondary text-white px-8 py-3 rounded-md font-semibold hover:bg-primaryGreen transition"
        >
          {data.cta?.text || "Commander"}
        </button>
      </div>

      {showPreCommandePopup && (
        <PreCommandePopup
          onClose={() => setShowPreCommandePopup(false)}
          parcoursHref={commanderHref}
        />
      )}
    </section>
  );
}